import { useState, useEffect, useCallback } from "react";
import { getLoginHistory, deleteLoginHistory, getActiveSessions, logoutAllUsers, forceLogoutUser, getFailedLogins, clearFailedLogins, getBlocklist, addBlocklist, removeBlocklist, getBanHistory, getAutoLogoutSettings, updateAutoLogoutSettings } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { History, Loader2, User, Clock, Calendar, Trash2, LogOut, Users, Settings, Shield, Globe, LogOut as LogOutIcon, ShieldAlert, Lock, XCircle, Ban, Mail, Search } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { usePermissions } from "@/context/PermissionContext";

export default function LoginHistoryPage() {
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission("login_history", "delete");
  const canManageSessions = hasPermission("login_history", "manage_sessions");
  const canForceLogout = hasPermission("login_history", "force_logout");

  const [history, setHistory] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [autoLogout, setAutoLogout] = useState({ enabled: true, hour: 0, minute: 58 });
  const [loading, setLoading] = useState(true);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [savingAutoLogout, setSavingAutoLogout] = useState(false);
  const [forcingId, setForcingId] = useState(null);
  const [failedLogins, setFailedLogins] = useState([]);
  const [lockedAccounts, setLockedAccounts] = useState([]);
  const [failedLoading, setFailedLoading] = useState(true);
  const [clearFailedDialogOpen, setClearFailedDialogOpen] = useState(false);
  const [clearingFailed, setClearingFailed] = useState(false);
  const [blocklist, setBlocklist] = useState([]);
  const [banHistory, setBanHistory] = useState([]);
  const [failedSearch, setFailedSearch] = useState("");
  const [failedReason, setFailedReason] = useState("all");
  const [banningKey, setBanningKey] = useState(null);

  const fetchBlocklist = useCallback(async () => {
    if (!canManageSessions) return;
    try {
      const response = await getBlocklist();
      setBlocklist(response.data || []);
    } catch (error) {
      console.error("Failed to fetch blocklist:", error);
    }
  }, [canManageSessions]);

  const fetchBanHistory = useCallback(async () => {
    if (!canManageSessions) return;
    try {
      const response = await getBanHistory();
      setBanHistory(response.data || []);
    } catch (error) {
      console.error("Failed to fetch ban history:", error);
    }
  }, [canManageSessions]);

  const isBanned = (type, value) =>
    blocklist.some((b) => b.type === type && b.value === (type === "email" ? String(value).toLowerCase() : value));

  const handleBan = async (type, value) => {
    const key = `${type}:${value}`;
    setBanningKey(key);
    try {
      await addBlocklist(type, value, "Blocked from failed-login log");
      toast.success(`${type === "email" ? "Email" : "IP"} ${value} has been banned`);
      fetchBlocklist();
      fetchBanHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to ban");
    } finally {
      setBanningKey(null);
    }
  };

  const handleUnban = async (entry) => {
    setBanningKey(entry.id);
    try {
      await removeBlocklist(entry.id);
      toast.success(`${entry.value} unblocked`);
      fetchBlocklist();
      fetchBanHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unblock");
    } finally {
      setBanningKey(null);
    }
  };

  const filteredFailedLogins = failedLogins.filter((f) => {
    const q = failedSearch.trim().toLowerCase();
    const matchQ = !q || (f.email || "").toLowerCase().includes(q) || (f.ip_address || "").toLowerCase().includes(q);
    const matchReason = failedReason === "all" || f.reason === failedReason;
    return matchQ && matchReason;
  });

  const fetchFailedLogins = useCallback(async () => {
    if (!canManageSessions) { setFailedLoading(false); return; }
    try {
      const response = await getFailedLogins();
      setFailedLogins(response.data.attempts || []);
      setLockedAccounts(response.data.locked_accounts || []);
    } catch (error) {
      console.error("Failed to fetch failed logins:", error);
    } finally {
      setFailedLoading(false);
    }
  }, [canManageSessions]);

  const handleClearFailedLogins = async () => {
    setClearingFailed(true);
    try {
      await clearFailedLogins();
      toast.success("Failed login log cleared");
      setClearFailedDialogOpen(false);
      fetchFailedLogins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear log");
    } finally {
      setClearingFailed(false);
    }
  };

  const handleForceLogoutUser = async (session) => {
    setForcingId(session.user_id);
    try {
      await forceLogoutUser(session.user_id);
      toast.success(`${session.user_name || session.user_email} has been logged out`);
      fetchActiveSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to log out user");
    } finally {
      setForcingId(null);
    }
  };

  const fetchHistory = useCallback(async () => {
    try {
      const response = await getLoginHistory();
      setHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch login history:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchActiveSessions = useCallback(async () => {
    if (!canManageSessions) { setSessionsLoading(false); return; }
    try {
      const response = await getActiveSessions();
      setActiveSessions(response.data);
    } catch (error) {
      console.error("Failed to fetch active sessions:", error);
    } finally {
      setSessionsLoading(false);
    }
  }, [canManageSessions]);

  const fetchAutoLogoutSettings = useCallback(async () => {
    if (!canManageSessions) return;
    try {
      const response = await getAutoLogoutSettings();
      setAutoLogout({ enabled: response.data.enabled, hour: response.data.hour, minute: response.data.minute });
    } catch (error) {
      console.error("Failed to fetch auto-logout settings:", error);
    }
  }, [canManageSessions]);

  useEffect(() => {
    fetchHistory();
    fetchActiveSessions();
    fetchAutoLogoutSettings();
    fetchFailedLogins();
    fetchBlocklist();
    fetchBanHistory();
  }, [fetchHistory, fetchActiveSessions, fetchAutoLogoutSettings, fetchFailedLogins, fetchBlocklist, fetchBanHistory]);

  // Auto-refresh live security data every 30s
  useEffect(() => {
    if (!canManageSessions) return;
    const interval = setInterval(() => {
      fetchActiveSessions();
      fetchFailedLogins();
      fetchBlocklist();
      fetchBanHistory();
    }, 30000);
    return () => clearInterval(interval);
  }, [canManageSessions, fetchActiveSessions, fetchFailedLogins, fetchBlocklist, fetchBanHistory]);

  const handleDeleteHistory = async () => {
    setDeleting(true);
    try {
      await deleteLoginHistory();
      toast.success("Login history deleted successfully");
      setDeleteDialogOpen(false);
      setHistory([]);
    } catch (error) {
      toast.error("Failed to delete login history");
    } finally {
      setDeleting(false);
    }
  };

  const handleLogoutAll = async () => {
    setLoggingOut(true);
    try {
      const response = await logoutAllUsers();
      toast.success(response.data.message || "All users have been logged out");
      setLogoutDialogOpen(false);
      setActiveSessions([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to logout all users");
    } finally {
      setLoggingOut(false);
    }
  };

  const handleAutoLogoutSave = async (newSettings) => {
    setSavingAutoLogout(true);
    try {
      await updateAutoLogoutSettings(newSettings);
      setAutoLogout(newSettings);
      toast.success(`Auto-logout ${newSettings.enabled ? `scheduled at ${String(newSettings.hour).padStart(2, '0')}:${String(newSettings.minute).padStart(2, '0')}` : 'disabled'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update auto-logout settings");
    } finally {
      setSavingAutoLogout(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6" data-testid="login-history-page">
      {/* Sticky Header */}
      <div className="sticky-page-header flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#0f172a] dark:text-white font-['Outfit']">Login History</h1>
          <p className="text-[#64748b] dark:text-gray-400 mt-1">Track user login activity & manage sessions</p>
        </div>
        <div className="flex items-center gap-3">
          {canForceLogout && activeSessions.length > 0 && (
            <Button
              variant="destructive"
              onClick={() => setLogoutDialogOpen(true)}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="logout-all-users-btn"
            >
              <LogOut size={16} className="mr-2" />
              Log Out All Users
            </Button>
          )}
          {canDelete && history.length > 0 && (
            <Button
              variant="destructive"
              onClick={() => setDeleteDialogOpen(true)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-history-btn"
            >
              <Trash2 size={16} className="mr-2" />
              Delete History
            </Button>
          )}
          <Badge variant="secondary" className="bg-[#f1f5f9] text-[#64748b]">
            {history.length} records
          </Badge>
        </div>
      </div>

      {/* Active Sessions Section */}
      {canManageSessions && (
        <Card className="border-[#e2e8f0] dark:border-gray-700" data-testid="active-sessions-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                  <Users size={20} className="text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <CardTitle className="text-lg text-[#0f172a] dark:text-white">Active Sessions</CardTitle>
                  <p className="text-sm text-[#64748b] dark:text-gray-400">Currently logged-in users</p>
                </div>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                {sessionsLoading ? "..." : activeSessions.length} online
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {sessionsLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-[#d4af37]" />
              </div>
            ) : activeSessions.length === 0 ? (
              <div className="text-center py-6 text-[#64748b] dark:text-gray-400">
                <Users className="mx-auto mb-2 opacity-40" size={32} />
                <p>No active sessions</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#0f172a] hover:bg-[#0f172a]">
                      <TableHead className="text-white font-semibold">User</TableHead>
                      <TableHead className="text-white font-semibold">Email</TableHead>
                      <TableHead className="text-white font-semibold">Role</TableHead>
                      <TableHead className="text-white font-semibold">IP Address</TableHead>
                      <TableHead className="text-white font-semibold">Login Time</TableHead>
                      <TableHead className="text-white font-semibold">Expires At</TableHead>
                      {canForceLogout && <TableHead className="text-white font-semibold text-right">Action</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeSessions.map((session, index) => (
                      <TableRow
                        key={session.id}
                        className="hover:bg-[#f8fafc] dark:hover:bg-gray-800"
                        data-testid={`session-row-${index}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <div className="w-8 h-8 bg-[#0f172a] dark:bg-gray-700 rounded-full flex items-center justify-center">
                                <User size={14} className="text-[#d4af37]" />
                              </div>
                              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white dark:border-gray-900" />
                            </div>
                            <span className="font-medium text-[#0f172a] dark:text-white">{session.user_name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-[#64748b] dark:text-gray-400">{session.user_email}</TableCell>
                        <TableCell>
                          <Badge
                            className={
                              session.role === "master_admin"
                                ? "bg-[#d4af37]/10 text-[#d4af37] border border-[#d4af37]/30"
                                : session.role === "admin"
                                ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                                : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                            }
                          >
                            {session.role === "master_admin" ? "Master Admin" : session.role === "admin" ? "Admin" : "Agent"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-[#64748b] dark:text-gray-400">
                            <Globe size={14} />
                            <span className="font-mono text-sm" data-testid={`session-ip-${index}`}>
                              {session.ip_address || "—"}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-[#64748b] dark:text-gray-400">
                            <Clock size={14} />
                            <span className="font-mono text-sm">
                              {format(parseISO(session.login_time), "dd MMM yyyy HH:mm")}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm text-[#64748b] dark:text-gray-400">
                            {format(parseISO(session.expires_at), "dd MMM yyyy HH:mm")}
                          </span>
                        </TableCell>
                        {canForceLogout && (
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={forcingId === session.user_id || session.role === "master_admin"}
                              onClick={() => handleForceLogoutUser(session)}
                              className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950"
                              data-testid={`force-logout-btn-${index}`}
                              title={session.role === "master_admin" ? "Master Admin cannot be force-logged out here" : "Force logout this user"}
                            >
                              {forcingId === session.user_id ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <><LogOutIcon size={14} className="mr-1" /> Logout</>
                              )}
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Failed Login Attempts Section */}
      {canManageSessions && (
        <Card className="border-[#e2e8f0] dark:border-gray-700" data-testid="failed-logins-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-950 flex items-center justify-center">
                  <ShieldAlert className="text-red-600 dark:text-red-400" size={20} />
                </div>
                <div>
                  <CardTitle className="text-lg text-[#0f172a] dark:text-white">Failed Login Attempts</CardTitle>
                  <p className="text-sm text-[#64748b] dark:text-gray-400">Recent failed sign-in attempts (last 200) · auto-refreshes every 30s</p>
                </div>
              </div>
              {failedLogins.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setClearFailedDialogOpen(true)}
                  className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950"
                  data-testid="clear-failed-logins-btn"
                >
                  <Trash2 size={14} className="mr-1" /> Clear Log
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Currently locked accounts banner */}
            {lockedAccounts.length > 0 && (
              <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40 p-3" data-testid="locked-accounts-banner">
                <div className="flex items-center gap-2 mb-2 text-amber-700 dark:text-amber-400 font-semibold text-sm">
                  <Lock size={15} /> Currently Locked ({lockedAccounts.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {lockedAccounts.map((la, i) => (
                    <Badge key={i} variant="outline" className="border-amber-400 text-amber-700 dark:text-amber-300" data-testid={`locked-account-${i}`}>
                      {la.email} · unlocks in ~{Math.ceil((la.retry_after_seconds || 0) / 60)} min
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Filters */}
            {failedLogins.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <div className="relative flex-1">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
                  <Input
                    value={failedSearch}
                    onChange={(e) => setFailedSearch(e.target.value)}
                    placeholder="Search email or IP..."
                    className="pl-9 bg-white dark:bg-gray-800 border-[#e2e8f0] dark:border-gray-700"
                    data-testid="failed-search-input"
                  />
                </div>
                <Select value={failedReason} onValueChange={setFailedReason}>
                  <SelectTrigger className="w-full sm:w-52 bg-white dark:bg-gray-800 border-[#e2e8f0] dark:border-gray-700" data-testid="failed-reason-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All reasons</SelectItem>
                    <SelectItem value="invalid_password">Wrong password</SelectItem>
                    <SelectItem value="unknown_account">Unknown account</SelectItem>
                    <SelectItem value="account_locked">Locked (blocked)</SelectItem>
                    <SelectItem value="account_disabled">Account disabled</SelectItem>
                    <SelectItem value="banned">Banned</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {failedLoading ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="animate-spin text-[#64748b]" size={24} /></div>
            ) : filteredFailedLogins.length === 0 ? (
              <div className="text-center py-8 text-[#64748b] dark:text-gray-400">
                <Shield className="mx-auto mb-2 opacity-40" size={32} />
                <p className="text-sm">{failedLogins.length === 0 ? "No failed login attempts recorded" : "No attempts match your filters"}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-red-600/90 hover:bg-red-600/90">
                      <TableHead className="text-white font-semibold">Email Entered</TableHead>
                      <TableHead className="text-white font-semibold">IP Address</TableHead>
                      <TableHead className="text-white font-semibold">Reason</TableHead>
                      <TableHead className="text-white font-semibold">Time</TableHead>
                      <TableHead className="text-white font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredFailedLogins.map((f, index) => {
                      const reasonMap = {
                        invalid_password: { label: "Wrong password", cls: "border-red-400 text-red-600 dark:text-red-400" },
                        unknown_account: { label: "Unknown account", cls: "border-orange-400 text-orange-600 dark:text-orange-400" },
                        account_locked: { label: "Locked (blocked)", cls: "border-amber-400 text-amber-600 dark:text-amber-400" },
                        account_disabled: { label: "Account disabled", cls: "border-gray-400 text-gray-600 dark:text-gray-400" },
                        banned: { label: "Banned", cls: "border-red-600 text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/40" },
                      };
                      const r = reasonMap[f.reason] || { label: f.reason, cls: "border-gray-400 text-gray-600" };
                      const emailBanned = isBanned("email", f.email);
                      const ipBanned = isBanned("ip", f.ip_address);
                      return (
                        <TableRow key={f.id || index} className="border-[#e2e8f0] dark:border-gray-700" data-testid={`failed-login-row-${index}`}>
                          <TableCell className="font-medium text-[#0f172a] dark:text-white">{f.email}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-[#64748b] dark:text-gray-400">
                              <Globe size={14} /><span className="font-mono text-sm">{f.ip_address || "—"}</span>
                            </div>
                          </TableCell>
                          <TableCell><Badge variant="outline" className={r.cls}>{r.label}</Badge></TableCell>
                          <TableCell>
                            <span className="font-mono text-sm text-[#64748b] dark:text-gray-400">
                              {format(parseISO(f.attempt_time), "dd MMM yyyy HH:mm:ss")}
                            </span>
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <Button
                              size="sm" variant="outline"
                              disabled={emailBanned || banningKey === `email:${f.email}`}
                              onClick={() => handleBan("email", f.email)}
                              className="mr-2 h-8 border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950 disabled:opacity-50"
                              data-testid={`ban-email-btn-${index}`}
                              title={emailBanned ? "Email already banned" : "Ban this email"}
                            >
                              {banningKey === `email:${f.email}` ? <Loader2 size={13} className="animate-spin" /> : <><Mail size={13} className="mr-1" />{emailBanned ? "Banned" : "Ban Email"}</>}
                            </Button>
                            <Button
                              size="sm" variant="outline"
                              disabled={ipBanned || banningKey === `ip:${f.ip_address}` || !f.ip_address || f.ip_address === "Unknown"}
                              onClick={() => handleBan("ip", f.ip_address)}
                              className="h-8 border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950 disabled:opacity-50"
                              data-testid={`ban-ip-btn-${index}`}
                              title={ipBanned ? "IP already banned" : "Ban this IP"}
                            >
                              {banningKey === `ip:${f.ip_address}` ? <Loader2 size={13} className="animate-spin" /> : <><Ban size={13} className="mr-1" />{ipBanned ? "Banned" : "Ban IP"}</>}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Blocked Emails & IPs Section */}
      {canManageSessions && (
        <Card className="border-[#e2e8f0] dark:border-gray-700" data-testid="blocklist-card">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-950 flex items-center justify-center">
                <Ban className="text-red-600 dark:text-red-400" size={20} />
              </div>
              <div>
                <CardTitle className="text-lg text-[#0f172a] dark:text-white">Blocked Emails &amp; IP Addresses</CardTitle>
                <p className="text-sm text-[#64748b] dark:text-gray-400">Blocked entries are rejected at login ({blocklist.length} active) — IPs &amp; emails are auto-banned after 5 attempts with an unregistered email</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {blocklist.length === 0 ? (
              <div className="text-center py-6 text-[#64748b] dark:text-gray-400">
                <Ban className="mx-auto mb-2 opacity-40" size={28} />
                <p className="text-sm">No blocked emails or IPs</p>
              </div>
            ) : (
              <div className="space-y-2">
                {blocklist.map((b) => (
                  <div key={b.id} className="flex items-center justify-between gap-3 rounded-lg border border-[#e2e8f0] dark:border-gray-700 p-3" data-testid={`blocklist-item-${b.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <Badge variant="outline" className={b.type === "email" ? "border-blue-400 text-blue-600 dark:text-blue-400" : "border-purple-400 text-purple-600 dark:text-purple-400"}>
                        {b.type === "email" ? <Mail size={12} className="mr-1" /> : <Globe size={12} className="mr-1" />}{b.type.toUpperCase()}
                      </Badge>
                      <span className="font-mono text-sm text-[#0f172a] dark:text-white truncate">{b.value}</span>
                      <span className="text-xs text-[#94a3b8] truncate hidden sm:inline">by {b.created_by}</span>
                    </div>
                    <Button
                      size="sm" variant="outline"
                      disabled={banningKey === b.id}
                      onClick={() => handleUnban(b)}
                      className="h-8 border-emerald-300 text-emerald-600 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400"
                      data-testid={`unban-btn-${b.id}`}
                    >
                      {banningKey === b.id ? <Loader2 size={13} className="animate-spin" /> : <><XCircle size={13} className="mr-1" />Unblock</>}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}


      {/* Ban History Section */}
      {canManageSessions && (
        <Card className="border-[#e2e8f0] dark:border-gray-700" data-testid="ban-history-card">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <History className="text-slate-600 dark:text-slate-300" size={20} />
              </div>
              <div>
                <CardTitle className="text-lg text-[#0f172a] dark:text-white">Ban History</CardTitle>
                <p className="text-sm text-[#64748b] dark:text-gray-400">Audit trail of every ban &amp; unban — automatic and manual ({banHistory.length})</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {banHistory.length === 0 ? (
              <div className="text-center py-6 text-[#64748b] dark:text-gray-400">
                <History className="mx-auto mb-2 opacity-40" size={28} />
                <p className="text-sm">No ban history yet</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {banHistory.map((h, idx) => {
                  const isUnban = h.action === "unban" || h.action === "self_unban";
                  const isAuto = h.action === "auto_ban";
                  const actionLabel = h.action === "self_unban" ? "SELF-UNBAN" : isUnban ? "UNBANNED" : isAuto ? "AUTO-BAN" : "BANNED";
                  const actionClass = isUnban
                    ? "border-emerald-400 text-emerald-600 dark:text-emerald-400"
                    : isAuto
                    ? "border-orange-400 text-orange-600 dark:text-orange-400"
                    : "border-red-400 text-red-600 dark:text-red-400";
                  return (
                    <div key={h.id || idx} className="flex items-start justify-between gap-3 rounded-lg border border-[#e2e8f0] dark:border-gray-700 p-3" data-testid={`ban-history-row-${idx}`}>
                      <div className="flex flex-col gap-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className={actionClass}>{actionLabel}</Badge>
                          <Badge variant="outline" className={h.type === "email" ? "border-blue-400 text-blue-600 dark:text-blue-400" : "border-purple-400 text-purple-600 dark:text-purple-400"}>
                            {h.type === "email" ? <Mail size={12} className="mr-1" /> : <Globe size={12} className="mr-1" />}{(h.type || "").toUpperCase()}
                          </Badge>
                          <span className="font-mono text-sm text-[#0f172a] dark:text-white truncate">{h.value}</span>
                        </div>
                        <span className="text-xs text-[#64748b] dark:text-gray-400">{h.reason}</span>
                        <span className="text-xs text-[#94a3b8]">by {h.actor}{h.timestamp ? ` \u2022 ${new Date(h.timestamp).toLocaleString()}` : ""}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}


      {/* Auto-Logout Settings */}
      {canManageSessions && (
        <Card className="border-[#e2e8f0] dark:border-gray-700" data-testid="auto-logout-settings-card">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                <Settings size={20} className="text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <CardTitle className="text-lg text-[#0f172a] dark:text-white">Automatic Logout Schedule</CardTitle>
                <p className="text-sm text-[#64748b] dark:text-gray-400">All users are automatically logged out at the scheduled time daily</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-[#0f172a] dark:text-white">Enabled</span>
                <Switch
                  checked={autoLogout.enabled}
                  onCheckedChange={(checked) => handleAutoLogoutSave({ ...autoLogout, enabled: checked })}
                  disabled={savingAutoLogout}
                  data-testid="auto-logout-toggle"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#64748b] dark:text-gray-400">Time (UTC):</span>
                <Select
                  value={String(autoLogout.hour)}
                  onValueChange={(val) => handleAutoLogoutSave({ ...autoLogout, hour: parseInt(val) })}
                  disabled={!autoLogout.enabled || savingAutoLogout}
                >
                  <SelectTrigger className="w-[80px]" data-testid="auto-logout-hour">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => (
                      <SelectItem key={i} value={String(i)}>
                        {String(i).padStart(2, "0")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-lg font-bold text-[#0f172a] dark:text-white">:</span>
                <Select
                  value={String(autoLogout.minute)}
                  onValueChange={(val) => handleAutoLogoutSave({ ...autoLogout, minute: parseInt(val) })}
                  disabled={!autoLogout.enabled || savingAutoLogout}
                >
                  <SelectTrigger className="w-[80px]" data-testid="auto-logout-minute">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 60 }, (_, i) => (
                      <SelectItem key={i} value={String(i)}>
                        {String(i).padStart(2, "0")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {savingAutoLogout && <Loader2 className="w-4 h-4 animate-spin text-[#d4af37]" />}
              <div className="flex items-center gap-2 text-xs text-[#94a3b8] dark:text-gray-500">
                <Shield size={14} />
                <span>Editable by Master Admin & authorized Admins</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* History Table */}
      <Card className="border-[#e2e8f0] dark:border-gray-700">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <History size={20} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <CardTitle className="text-lg text-[#0f172a] dark:text-white">Login History</CardTitle>
              <p className="text-sm text-[#64748b] dark:text-gray-400">All login records</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-center py-8 text-[#64748b] dark:text-gray-400">
              <History className="mx-auto mb-2 opacity-40" size={32} />
              <h3 className="font-medium">No Login History</h3>
              <p className="text-sm">Login records will appear here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#0f172a] hover:bg-[#0f172a]">
                    <TableHead className="text-white font-semibold">User</TableHead>
                    <TableHead className="text-white font-semibold">Email</TableHead>
                    <TableHead className="text-white font-semibold">Login Time</TableHead>
                    <TableHead className="text-white font-semibold">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((record, index) => (
                    <TableRow
                      key={record.id}
                      className="hover:bg-[#f8fafc] dark:hover:bg-gray-800"
                      data-testid={`history-row-${index}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-[#0f172a] dark:bg-gray-700 rounded-full flex items-center justify-center">
                            <User size={14} className="text-[#d4af37]" />
                          </div>
                          <span className="font-medium text-[#0f172a] dark:text-white">{record.user_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-[#64748b] dark:text-gray-400">{record.user_email}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-[#64748b] dark:text-gray-400">
                          <Clock size={14} />
                          <span className="font-mono">
                            {format(parseISO(record.login_time), "HH:mm:ss")}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-[#64748b] dark:text-gray-400">
                          <Calendar size={14} />
                          <span>
                            {format(parseISO(record.login_time), "dd MMM yyyy")}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete History Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white dark:bg-gray-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0f172a] dark:text-white">Delete Login History</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete all login history? This will remove {history.length} records and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#e2e8f0]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteHistory}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-history-btn"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete All"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear Failed Logins Confirmation Dialog */}
      <AlertDialog open={clearFailedDialogOpen} onOpenChange={setClearFailedDialogOpen}>
        <AlertDialogContent className="bg-white dark:bg-gray-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0f172a] dark:text-white flex items-center gap-2">
              <ShieldAlert size={20} className="text-red-600" />
              Clear Failed Login Log
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all recorded failed login attempts ({failedLogins.length} shown). This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#e2e8f0]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearFailedLogins}
              disabled={clearingFailed}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-clear-failed-btn"
            >
              {clearingFailed ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Clearing...</>) : "Clear Log"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Logout All Users Confirmation Dialog */}
      <AlertDialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <AlertDialogContent className="bg-white dark:bg-gray-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0f172a] dark:text-white flex items-center gap-2">
              <LogOut size={20} className="text-orange-600" />
              Log Out All Users
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will immediately invalidate all active sessions. Every user (including yourself) will be required to log in again. Are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#e2e8f0]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLogoutAll}
              disabled={loggingOut}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="confirm-logout-all-btn"
            >
              {loggingOut ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Logging out...
                </>
              ) : (
                "Log Out Everyone"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
